// src/features/organisation/components/positions/PositionDetailForm.tsx
import React, { useMemo } from 'react';
import { Briefcase } from 'lucide-react';
import { Position } from '../../../../types/organisation';
import { useDepartments } from '../../hooks/useDepartments';
import {
  EntityFormLayout,
  EntityFormHeader,
  EntityFormFooter,
  EditableField
} from '../../../../components/common/forms';
import { useEntityForm, FieldConfig } from '../../../../components/common/hooks';
import { apiClient } from '../../../../services/api/client';

interface PositionDetailFormProps {
  positionId: number | null;
  onClose: () => void;
  onSave?: (position: Position) => void;
}

const PositionDetailForm: React.FC<PositionDetailFormProps> = ({
  positionId,
  onClose,
  onSave: onSaveCallback,
}) => {
  const { departments } = useDepartments();
  const [loading, setLoading] = React.useState(!!positionId);
  const [position, setPosition] = React.useState<Position | null>(null);

  React.useEffect(() => {
    if (positionId) {
      setLoading(true);
      apiClient.get(`/organisation/positions/${positionId}`)
        .then((data) => setPosition(data))
        .finally(() => setLoading(false));
    } else {
      setPosition(null);
    }
  }, [positionId]);

  const fields: FieldConfig[] = useMemo(() => [
    {
      name: 'positionCode',
      label: 'Code du poste',
      type: 'text',
      required: true,
      maxLength: 20,
      disabled: !!positionId,
    },
    {
      name: 'positionName',
      label: 'Nom du poste',
      type: 'text',
      required: true,
      maxLength: 255,
    },
    {
      name: 'departmentId',
      label: 'Département',
      type: 'select',
      required: true,
      options: departments.map(d => ({ value: d.id, label: d.departmentName })),
      render: (value) => departments.find(d => d.id === value)?.departmentName || 'Non défini',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      maxLength: 1000,
      colSpan: 2,
    },
  ], [departments, positionId]);

  const transformToFormData = (pos: Position | null) => {
    if (!pos) return { positionCode: '', positionName: '', departmentId: null, description: '' };
    return {
      positionCode: pos.positionCode,
      positionName: pos.positionName,
      departmentId: pos.department?.id,
      description: pos.description || '',
    };
  };

  const handleSave = async (formData: Record<string, unknown>) => {
    let saved: Position;
    if (positionId) {
      saved = await apiClient.put(`/organisation/positions/${positionId}`, formData);
    } else {
      saved = await apiClient.post('/organisation/positions', formData);
    }
    onSaveCallback?.(saved);
    onClose();
    return saved;
  };

  const {
    formData,
    hasChanges,
    saving,
    errors,
    handleFieldChange,
    startEditing,
    stopEditing,
    isEditing,
    handleSave: onFormSave,
  } = useEntityForm({
    entity: position,
    fields,
    onSave: handleSave,
    transformToFormData,
  });

  return (
    <EntityFormLayout loading={loading} maxWidth="4xl">
      <EntityFormHeader
        title={position ? position.positionName : 'Nouveau poste'}
        subtitle={position ? `Code: ${position.positionCode}` : undefined}
        icon={Briefcase}
        hasChanges={hasChanges}
        saving={saving}
        onSave={onFormSave}
        onClose={onClose}
      />

      <div className="flex-1 overflow-y-auto p-6">
        {formData && (
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Informations</h3>
            <div className="grid grid-cols-2 gap-4">
              {fields.map(field => (
                <div key={field.name} className={field.colSpan === 2 ? 'col-span-2' : ''}>
                  <EditableField
                    field={field}
                    value={formData[field.name]}
                    isEditing={isEditing(field.name)}
                    onStartEdit={() => startEditing(field.name)}
                    onStopEdit={() => stopEditing(field.name)}
                    onChange={(value) => handleFieldChange(field.name, value)}
                    error={errors[field.name]}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <EntityFormFooter helpText="💡 Double-cliquez sur un champ pour le modifier." />
    </EntityFormLayout>
  );
};

export default PositionDetailForm;
